import * as React from 'react';
import {Component} from 'react';
import {observer} from 'mobx-react';

import {ColumnType, ColumnZoomType} from 'priority-web-sdk/enums';
import {SearchControl} from './Controls/SearchControl';
import {RangeControl} from './Controls/RangeControl';
import {DateControl} from './Controls/DateControl';
import {
    Container,
    Flex,
    ToggleSwitch,
    Draggable,
    Select,
    IconItemTemplate,
    Separator,
    Title,
    PriorityIcon,
    Icon
} from '@priority-software/priority-style-react';
import {TextControl} from './Controls/TextControl';
import {BoolControl} from './Controls/BoolControl';
import {columnOptionsDisplay, OperatorIconMapping, Operator} from '../consts/operators';
import {deleteFilter} from '../actions/filters';
import {Filter} from '../stores/Filter';
import {openSearchField, changeSearchField, scrollSearchField} from '../actions/filters';
import {getLocalizedText, texts} from '../../../constants/TextConst';

import {DurationControl as DurationValidationControl} from '../../library/FieldControls/Duration';
import {NumberControl as NumberValidationControl} from '../../library/FieldControls/Number';
import {TimeControl as TimeValidationControl} from '../../library/FieldControls/Time';
import {TextValidationControl} from '../../library/FieldControls/Text';
import {InjectedDraggableProps} from '../../../components/Common/DraggableList';
import {withHover} from '../../../components/Common/HOC/withHover';
import filterTexts from '../../Filters/texts';
import {SortIcon} from '../../../components/Common/SortIcon';
import {TagsArea} from '../../../components/Common/TagsArea';
import {Label} from '../../../components/Common/Label';

const Sort = observer(({filter}) => (
    <Flex inline componentClasses="sort-row">
        <div>
            <Label skin="paragraph-2" textColor="primary-700" endPadding>
                {getLocalizedText(texts.filtersSort)}
            </Label>
        </div>

        <ToggleSwitch tabIndex={-1} checked={filter.issort} onChange={filter.toggleSort} />

        <SortIcon
            isDesc={filter.isdesc}
            onClick={filter.toggleDesc}
            componentClasses="sort-component"
        />
    </Flex>
));

interface FilterBoxProps extends InjectedDraggableProps {
    filter: Filter;
    isHovering?: boolean;
    fullDisplay?: boolean;
    alwaysFocusOnMount?: boolean;
    onClose: () => void;
}

const FilterBoxComp = props => {
    if (props.draggable) {
        return (
            <Draggable {...props} iconPosition="top">
                <FilterBoxContent {...props} />
            </Draggable>
        );
    } else {
        return <FilterBoxContent {...props} />;
    }
};

@observer
class FilterBoxContent extends Component<FilterBoxProps, any> {
    constructor(props) {
        super(props);

        this.state = {
            active: false
        };
    }

    setActive = () => {
        this.setState({active: true});
    };

    unsetActive = () => {
        this.setState({active: false});
    };

    removeTag = index => {
        const {filter} = this.props;
        filter.removeValue(index);
    };

    setOperator = (operator, {action}) => {
        if (action === 'select-option') {
            const {filter} = this.props;
            filter.setOperator(operator.value);
            if (
                operator.value === Operator.Empty ||
                operator.value === Operator.NotEmpty ||
                filter.column.type === ColumnType.Date
            ) {
                filter.setValue(null);
                filter.setValues([]);
            }
        }
    };

    deleteFilterBox = () => {
        const {filter} = this.props;
        deleteFilter(filter);
    };

    selectType() {
        const {filter, onClose} = this.props;
        const {column, field, value, values, operator} = filter;
        const handleTextControlPaste = ({values}) => {
            values.forEach(value => filter.addValue(value));
        };
        let Control;
        let props;
        const isDisabled = operator === Operator.NotEmpty || operator === Operator.Empty;

        switch (column.type) {
            case ColumnType.Date:
                Control = DateControl;
                props = {
                    ...column,
                    onChange: filter.setValue,
                    value,
                    isDisabled
                };
                break;
            case ColumnType.Time:
                Control = RangeControl;
                props = {
                    ...column,
                    operator,
                    validationControl: TimeValidationControl,
                    onChange: filter.setValue,
                    fromLabel: getLocalizedText(texts.from),
                    toLabel: getLocalizedText(texts.to),
                    value,
                    isDisabled
                };
                break;
            case ColumnType.Duration:
                Control = RangeControl;
                props = {
                    ...column,
                    operator,
                    validationControl: DurationValidationControl,
                    onChange: filter.setValue,
                    fromLabel: getLocalizedText(texts.from),
                    toLabel: getLocalizedText(texts.to),
                    value,
                    isDisabled
                };
                break;
            case ColumnType.Bool:
                Control = BoolControl;
                props = {
                    selected: value,
                    onChange: filter.setValue,
                    options: [
                        {label: filterTexts.yes, value: true},
                        {label: filterTexts.no, value: false}
                    ]
                };
                break;
            default:
                switch (column.zoom) {
                    case ColumnZoomType.Choose:
                    case ColumnZoomType.Search:
                        if (operator === Operator.Equals || operator === Operator.NotEquals) {
                            Control = SearchControl;
                            const placeholder = `${getLocalizedText(
                                texts.searchSelectPlaceholder
                            )} ${column.title.toLowerCase()}`;
                            props = {
                                onSelect: filter.setValues,
                                onSearch: (...args) => changeSearchField(field, ...args),
                                onSearchNext: (...args) => scrollSearchField(field, ...args),
                                initFieldSearch: (...args) => openSearchField(field, ...args),
                                addValue: filter.addValue,
                                onChoose: filter.toggleValue,
                                isMultiSelect: true,
                                isValueChecked: filter.isValueExists,
                                placeholder,
                                value: values,
                                onClose: values.length ? onClose : null,
                                isDisabled
                            };
                        } else {
                            Control = TextControl;
                            props = {
                                ...column,
                                handleSpreadsheetValues: handleTextControlPaste,
                                onChange: filter.addValue,
                                value,
                                operator,
                                isDisabled
                            };
                        }
                        break;
                    default:
                        switch (column.type) {
                            case ColumnType.Number:
                                Control = RangeControl;
                                props = {
                                    ...column,
                                    operator,
                                    validationControl: NumberValidationControl,
                                    onChange: filter.setValue,
                                    fromLabel: getLocalizedText(texts.filterMin),
                                    toLabel: getLocalizedText(texts.filtersMax),
                                    value,
                                    isDisabled
                                };
                                break;

                            default:
                                if (operator === Operator.Between) {
                                    Control = RangeControl;
                                    props = {
                                        ...column,
                                        operator,
                                        validationControl: TextValidationControl,
                                        onChange: filter.setValue,
                                        fromLabel: getLocalizedText(texts.from),
                                        toLabel: getLocalizedText(texts.to),
                                        value
                                    };
                                } else {
                                    Control = TextControl;
                                    props = {
                                        ...column,
                                        handleSpreadsheetValues: handleTextControlPaste,
                                        onChange: filter.addValue,
                                        value,
                                        operator,
                                        isDisabled
                                    };
                                }
                        }
                }
        }
        return {Control: Control, props};
    }

    renderOperators(operators, selected) {
        const options = operators.map(op => ({
            value: op.type,
            label: getLocalizedText(op.name),
            icon: op.icon
        }));

        const Item = (data, isSelected) => {
            if (data.value === Operator.Empty) {
                return (
                    <>
                        <Separator
                            align="vertical"
                            height={1}
                            componentClasses="operators-separator"
                        />
                        {IconItemTemplate(data, isSelected)}
                    </>
                );
            } else {
                return IconItemTemplate(data, isSelected);
            }
        };

        const value = {
            value: selected.type,
            label: getLocalizedText(selected.name),
            icon: selected.icon
        };

        return (
            <Select
                options={options}
                onFocus={this.setActive}
                onBlur={this.unsetActive}
                onMenuOpen={this.setActive}
                onMenuClose={this.unsetActive}
                onChange={this.setOperator}
                value={value}
                renderItem={Item}
                selectMenuClasses="ignore-react-onclickoutside"
                componentClasses="operators-select"
                isSearchable={false}
                placeholder={<></>}
            />
        );
    }

    render() {
        const {filter, fullDisplay, alwaysFocusOnMount} = this.props;
        const {column, values, operator, isEmpty, isLast} = filter;
        const autoFocus = alwaysFocusOnMount || (isEmpty && isLast);
        const operators = columnOptionsDisplay(column);
        const {Control, props} = this.selectType();
        const selectedOperator = OperatorIconMapping[operator];

        return (
            <Container
                roundConer
                onClose={fullDisplay && this.deleteFilterBox}
                componentClasses="wrap-filter-box filter-box"
            >
                {fullDisplay && (
                    <Flex justifyContent="space-between" alignItems="center">
                        <Title skin="h6" textColor="primary-700" componentClasses="container-title">
                            {column.title}
                        </Title>
                        <Icon
                            icon={PriorityIcon.closeBig}
                            onClick={this.deleteFilterBox}
                            skin="standart"
                            style={{zIndex: 10}}
                        />
                    </Flex>
                )}
                <Flex componentClasses="control-row" key={filter.field}>
                    {column.type === ColumnType.Bool ? (
                        <Control
                            {...props}
                            setActive={this.setActive}
                            unsetActive={this.unsetActive}
                            autoFocus={autoFocus}
                        />
                    ) : (
                        <>
                            {this.renderOperators(operators, selectedOperator)}
                            <Control
                                {...props}
                                operator={filter.operator}
                                setActive={this.setActive}
                                unsetActive={this.unsetActive}
                                autoFocus={autoFocus}
                            />
                        </>
                    )}
                </Flex>
                <Flex>
                    {operator === Operator.Between ? (
                        <Label skin="paragraph-2" textColor="primary-700">
                            {column.type === ColumnType.Text
                                ? filterTexts.mustFillAll
                                : getLocalizedText(texts.filtersRangeOneField)}
                        </Label>
                    ) : (
                        <TagsArea
                            tags={values}
                            fullDisplay={fullDisplay}
                            removeTag={this.removeTag}
                        />
                    )}
                    {fullDisplay && <Sort filter={filter} />}
                </Flex>
            </Container>
        );
    }
}

export const FilterBox = withHover(FilterBoxComp);
